/**
 * WorkflowPage Component
 *
 * This component implements the workflow selection interface for the Migration Utility application.
 * It provides cards for different migration types:
 * 1. Ontology Migration (active)
 * 2. Process Migration (coming soon)
 *
 * When a user selects Ontology Migration, it creates a new workflow and navigates to the detail page.
 *
 * @module pages/WorkflowPage
 */

import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Database, FileText, History, Loader2, Server, Users, Zap } from "lucide-react";
import { useStartMigration } from "../hooks/useStartMigration";
import { usePairCapabilities } from "../lib/api";
import { AccessSummary } from "../components/AccessSummary";
import { AppSidebar } from "../components/app-sidebar";
import { InstancePairBadge } from "../components/InstancePicker";
import { NoPermissionModal } from "../components/NoPermissionModal";
import { SiteHeader } from "../components/site-header";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import { useInstances } from "../lib/api-central-mu";
import { useAuthStore } from "../store/useAuthStore";
import { useInstancePairStore } from "../store/useInstancePairStore";

/**
 * WorkflowPage Component
 *
 * @returns {JSX.Element} The rendered WorkflowPage component
 */
const WorkflowPage: React.FC = () => {
  // Navigation hook (used by the Linked Process card, which routes to a static page)
  const navigate = useNavigate();

  // The DEFAULT admin is a management-only account: the backend reports no migration
  // capabilities for it, and this page shows the admin tools instead of migration cards.
  const { isDefaultUser } = useAuthStore();

  // The source→target pair chosen on the select-instances step. Non-admin users must pick it
  // before this page — capabilities are pair-scoped (the operator may hold different roles per
  // instance), so without a pair there is nothing to gate the cards on.
  const sourceServiceId = useInstancePairStore((s) => s.sourceServiceId);
  const targetServiceId = useInstancePairStore((s) => s.targetServiceId);
  const pairMissing = !isDefaultUser && (sourceServiceId == null || targetServiceId == null);

  const { data: instances = [] } = useInstances();
  const sourceName = instances.find((i) => i.id === sourceServiceId)?.name ?? null;
  const targetName = instances.find((i) => i.id === targetServiceId)?.name ?? null;

  // Operator's migration capabilities for the selected pair: account ACTIVE and permission held
  // on BOTH instances. Drives which workflow cards are enabled and the no-permission popup.
  const { data: caps, isSuccess: capsLoaded } = usePairCapabilities(sourceServiceId, targetServiceId);
  const canMigrateProcess = !!caps?.canMigrateProcess;
  const canMigrateOntology = !!caps?.canMigrateOntology;
  const checklistCount = (canMigrateOntology ? 1 : 0) + (canMigrateProcess ? 1 : 0);
  const [noPermissionDismissed, setNoPermissionDismissed] = React.useState(false);
  // The admin having no migration capability is by design — never show it the popup.
  const showNoPermission =
    capsLoaded && !isDefaultUser && !canMigrateProcess && !canMigrateOntology && !noPermissionDismissed;

  // A workflow card is hidden outright when the operator lacks that permission for the selected
  // pair — a card the user can't act on shouldn't be shown at all. When they hold NO migration
  // permission on the pair, the NoPermissionModal below explains it. Sections with no visible cards
  // are hidden too (see the capsLoaded && can… guards on each <section>).

  const adminTools = [
    {
      title: "Users",
      description: "Create MU users, manage invites, and sync their per-instance access.",
      icon: Users,
      url: "/users",
    },
    {
      title: "Connected Services",
      description: "Register Leucine instances and manage their credentials.",
      icon: Server,
      url: "/connected-services",
    },
    {
      title: "Session Activity",
      description: "Review login and logout activity across all MU users.",
      icon: History,
      url: "/session-activity",
    },
  ];

  // Shared "start a fresh migration" logic (generate code + navigate to detail page).
  const {
    isGenerating: isGeneratingCode,
    startOntology: handleCreateOntologyWorkflow,
    startProcess: handleCreateProcessWorkflow,
    startActionEffect: handleCreateActionEffectWorkflow,
  } = useStartMigration();

  // Deep links / cleared sessionStorage: without a pair there is nothing to gate on — go pick one.
  if (pairMissing) {
    return <Navigate to="/select-instances" replace />;
  }

  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "16rem",
        "--header-height": "3rem",
      } as React.CSSProperties}
      className="min-h-screen"
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="pt-0">
        <SiteHeader />
        <main className="mx-auto w-full max-w-6xl px-6 py-10">
          {/* Page header */}
          <header className="mb-8">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              {isDefaultUser ? "Administration" : "Select Migration Type"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {isDefaultUser
                ? "Manage MU users, connected instances, and session activity. Migrations are performed by instance-linked users."
                : "Choose the type of migration you want to perform."}
            </p>
            {!isDefaultUser && (
              <div className="mt-4 flex items-center gap-3">
                <InstancePairBadge sourceName={sourceName} targetName={targetName} />
                <button
                  onClick={() => navigate("/select-instances")}
                  className="text-xs font-medium text-muted-foreground underline underline-offset-2 transition hover:text-foreground"
                >
                  Change instances
                </button>
              </div>
            )}
          </header>

          {/* At-a-glance: which instances this operator can migrate between */}
          <AccessSummary />

          {/* ────── Admin tools (DEFAULT admin is management-only; no migration cards) ────── */}
          {isDefaultUser && (
            <section className="mb-14">
              <div className="mb-5 flex items-baseline justify-between border-b border-border pb-3">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/70">Admin tools</p>
                  <h2 className="mt-1 text-base font-medium text-foreground">Manage the Migration Utility</h2>
                </div>
                <span className="text-xs text-muted-foreground/70">{adminTools.length} available</span>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {adminTools.map((tool) => (
                  <article
                    key={tool.url}
                    className="group relative rounded-xl border border-border bg-card/60 p-5 transition hover:border-border/70 hover:bg-card/80"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/60 text-foreground/80">
                        <tool.icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-medium text-foreground">{tool.title}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">{tool.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(tool.url)}
                      className="mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-medium text-background transition hover:bg-foreground/90"
                    >
                      Open
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* ────── Checklist-based section (hidden when the pair grants neither permission) ────── */}
          {!isDefaultUser && capsLoaded && (canMigrateOntology || canMigrateProcess) && (
          <section className="mb-10">
            <div className="mb-5 flex items-baseline justify-between border-b border-border pb-3">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/70">Checklist-based</p>
                <h2 className="mt-1 text-base font-medium text-foreground">Driven by a checklist or process</h2>
              </div>
              <span className="text-xs text-muted-foreground/70">{checklistCount} available</span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {/* Ontology Migration — shown only when the pair grants the ontology permission */}
              {canMigrateOntology && (
              <article className="group relative rounded-xl border border-border bg-card/60 p-5 transition hover:border-border/70 hover:bg-card/80">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/60 text-foreground/80">
                    <Database className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-foreground">Ontology Migration</h3>
                    <p className="mt-1 text-xs text-muted-foreground">Migrate data structures, schemas, and relationships.</p>
                  </div>
                </div>

                <ul className="mt-5 space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />
                    Object types and their properties
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />
                    Relationships between objects
                  </li>
                </ul>

                <button
                  onClick={handleCreateOntologyWorkflow}
                  disabled={isGeneratingCode}
                  className="mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-medium text-background transition hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isGeneratingCode ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    <>
                      Start migration
                      <ArrowRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </button>
              </article>
              )}

              {/* Process Migration — shown only when the pair grants the process permission */}
                {canMigrateProcess && (
                <article className="group relative rounded-xl border border-border bg-card/60 p-5 transition hover:border-border/70 hover:bg-card/80">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/60 text-foreground/80">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-foreground">Process Migration</h3>
                      <p className="mt-1 text-xs text-muted-foreground">Migrate business processes and workflows.</p>
                    </div>
                  </div>

                  <ul className="mt-5 space-y-2 text-xs text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />
                      Business process definitions
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />
                      Workflow rules and conditions
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />
                      Linked child checklists resolved automatically
                    </li>
                  </ul>

                  <button
                    onClick={handleCreateProcessWorkflow}
                    disabled={isGeneratingCode}
                    className="mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-medium text-background transition hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isGeneratingCode ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Creating…
                      </>
                    ) : (
                      <>
                        Start migration
                        <ArrowRight className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                </article>
                )}

            </div>
          </section>
          )}

          {/* ────── Ontology-based section (hidden when the pair lacks the ontology permission) ────── */}
          {!isDefaultUser && capsLoaded && canMigrateOntology && (
          <section>
            <div className="mb-5 flex items-baseline justify-between border-b border-border pb-3">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/70">Ontology-based</p>
                <h2 className="mt-1 text-base font-medium text-foreground">Independent of any checklist</h2>
              </div>
              <span className="text-xs text-muted-foreground/70">{canMigrateOntology ? 1 : 0} available</span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Action Effect — ontology-side migration, shown on the same ontology permission */}
              <article className="group relative rounded-xl border border-border bg-card/60 p-5 transition hover:border-border/70 hover:bg-card/80">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/60 text-foreground/80">
                    <Zap className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-foreground">Action Effect Migration</h3>
                    <p className="mt-1 text-xs text-muted-foreground">Migrate automation actions and their effects.</p>
                  </div>
                </div>
                <ul className="mt-5 space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />
                    Automation triggers and conditions
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />
                    Resulting effects on objects and parameters
                  </li>
                </ul>

                <button
                  onClick={handleCreateActionEffectWorkflow}
                  disabled={isGeneratingCode}
                  className="mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-medium text-background transition hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isGeneratingCode ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    <>
                      Start migration
                      <ArrowRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </button>
              </article>
            </div>
          </section>
          )}

          <NoPermissionModal open={showNoPermission} onClose={() => setNoPermissionDismissed(true)} instances={caps?.instances ?? []} />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default WorkflowPage;
